import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import * as THREE from 'three';
import toast from 'react-hot-toast';
import store from '../../../../../redux/store';
import {
  lightDataUpdate,
  update_assets_data,
  removeLight,
  selected_object,
  addLightToOtherObject,
  removeCamera,
  removeIntersectedObject,
  removeSceneObject,
} from '../../../../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../../../../pages/Dashboard/GridPage';
import { getCameraStableId } from '../../../../../utils/ThreeJsUtils/sceneCameraUtils';
import {
  startAllSceneAnimations,
  stopAllSceneAnimations,
  registerPreviewAnimationRefs,
} from '../../../../../utils/preview/startAllSceneAnimations';
import {
  removeParticle,
  setSelectedParticle,
} from '../../../../../redux/Slice/particleSlice';
import { extractMeshesFromGLB } from '../../../../gridpagefunction/MeshExtractation';
import { setSelectedLightId } from '../../../../../redux/Slice/globalStates';
import {
  collectAssetLookupIds,
  hasDisplayName,
} from '../../../../../utils/gridHistoryAssetsSync';
import { cleanupParticleSystem } from '../../../../../utils/particles/particleCleanupUtils.js';

export const useAppLeftPanel = ({
  scene,
  selectedObject,
  setSelectedObjectState,
  objectt,
  lastSavedSceneSnapshot,
  transformControls,
}) => {
  const { animationObjects } = useSelector((state) => state.animation);
  const { assets_data, select_object, light_data, camera_properties } =
    useSelector((state) => state.assets_object);
  const particles = useSelector((state) => state.particles.particles);
  
  const { isPublishing } = useSelector((state) => state.global_state);
  const selectedLightId = useSelector(
    (state) => state.global_state.selectedLightId
  );
  const dispatch = useDispatch();

  // Track active animations for replay functionality
  const activeAnimationsRef = useRef(new Map());
  const previewTimelineRafRef = useRef(null);

  useEffect(() => {
    registerPreviewAnimationRefs(activeAnimationsRef, previewTimelineRafRef);
  }, []);

  // State management
  const [selectedPop, setselectedPop] = useState();
  const [collapsedItems, setCollapsedItems] = useState(new Set());
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [projectConfirmation, setProjectConfirmation] = useState(false);
  const [children, setChildren] = useState(scene?.children || []);
  const [lastSaved, setLastSaved] = useState(lastSavedSceneSnapshot);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [renamingItem, setRenamingItem] = useState(null);
  const [expandedGLBs, setExpandedGLBs] = useState(new Set());
  const [glbMeshes, setGLBMeshes] = useState({});
  const [selectedGLBMeshPop, setSelectedGLBMeshPop] = useState(null);
  const [glbMeshSubmenuPosition, setGLBMeshSubmenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [selectedLightPop, setSelectedLightPop] = useState(null);
  const [isRenamingLight, setIsRenamingLight] = useState(false);
  const [renamingLight, setRenamingLight] = useState(null);
  const [newLightName, setNewLightName] = useState('');
  const [lightSubmenuPosition, setLightSubmenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [showLightDeleteConfirmation, setShowLightDeleteConfirmation] =
    useState(false);
  const [lightToDelete, setLightToDelete] = useState(null);
  const [showParticleDeleteConfirmation, setShowParticleDeleteConfirmation] =
    useState(false);
  const [particleToDelete, setParticleToDelete] = useState(null);

  const [copiedItem, setCopiedItem] = useState(null);
  const [selectedCameraPop, setSelectedCameraPop] = useState(null);
  const [cameraSubmenuPosition, setCameraSubmenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [showCameraDeleteConfirmation, setShowCameraDeleteConfirmation] =
    useState(false);
  const [cameraToDelete, setCameraToDelete] = useState(null);
  const [cameraRemoved, setCameraRemoved] = useState(false);

  // Drag & Drop state
  const [draggingAssetId, setDraggingAssetId] = useState(null);
  const [hoverTargetAssetId, setHoverTargetAssetId] = useState(null);
  const [collapsedMap, setCollapsedMap] = useState({});
  const [draggedFileName, setDraggedFileName] = useState('');

  // Animation control functions — shared preview clock like Design timeline
  const stopAllAnimations = () => {
    stopAllSceneAnimations(activeAnimationsRef, previewTimelineRafRef);
  };

  const startAllAnimations = () => {
    startAllSceneAnimations(scene, animationObjects, activeAnimationsRef, {
      previewTimelineRafRef,
    });
  };

  // Light management functions
  const handleRemoveLight = (id) => {
    if (light_data.length === 1) {
      setLightToDelete(id);
      setShowLightDeleteConfirmation(true);
      setSelectedLightPop(null);
      return;
    }
    dispatch(removeLight(id));
    if (id === selectedLightId) {
      const remaining = light_data.filter((l) => l.id !== id);
      dispatch(setSelectedLightId(remaining[0]?.id || null));
    }
  };

  const confirmLightDeletion = () => {
    if (lightToDelete) {
      dispatch(removeLight(lightToDelete));
      if (lightToDelete === selectedLightId) {
        dispatch(setSelectedLightId(null));
      }
      setLightToDelete(null);
    }
    setShowLightDeleteConfirmation(false);
  };

  // Camera management functions
  const handleRemoveCamera = (id) => {
    // Prevent deleting the last remaining camera – show toast instead
    if (!camera_properties || camera_properties.length <= 1) {
      toast.error(
        'Cannot delete the last camera. At least one camera must remain.'
      );
      setSelectedCameraPop(null);
      return;
    }

    // For other cameras, show confirmation modal first
    setCameraToDelete(id);
    setShowCameraDeleteConfirmation(true);
    setSelectedCameraPop(null);
  };

  const confirmCameraDeletion = () => {
    if (!cameraToDelete) {
      setShowCameraDeleteConfirmation(false);
      return;
    }

    // Remove from Redux state
    dispatch(removeCamera(cameraToDelete));

    // Remove camera from Three.js scene
    const cameraObj = scene.getObjectByName(cameraToDelete);

    // Also remove any helpers and points associated with this camera
    const cameraHelper = scene.getObjectByName(cameraToDelete + 'Helper');
    const cameraPoint = scene.getObjectByName(cameraToDelete + 'Point');

    if (cameraHelper) {
      if (cameraHelper.parent) cameraHelper.parent.remove(cameraHelper);
      else scene.remove(cameraHelper);
      dispatch(removeIntersectedObject(cameraHelper));
      if (cameraHelper.dispose) cameraHelper.dispose();
    }

    if (cameraPoint) {
      if (cameraPoint.parent) cameraPoint.parent.remove(cameraPoint);
      else scene.remove(cameraPoint);
      dispatch(removeIntersectedObject(cameraPoint));
      if (cameraPoint.geometry) cameraPoint.geometry.dispose();
      if (cameraPoint.material) {
        if (Array.isArray(cameraPoint.material)) {
          cameraPoint.material.forEach((m) => m.dispose());
        } else {
          cameraPoint.material.dispose();
        }
      }
    }

    if (cameraObj && cameraObj.parent) {
      cameraObj.parent.remove(cameraObj);
    }

    // Handle selection if deleted camera was selected
    if (select_object?.name === cameraToDelete) {
      const remainingCameras = (camera_properties || []).filter(
        (c) => c._id !== cameraToDelete && c.id !== cameraToDelete
      );

      if (remainingCameras.length > 0) {
        const nextCamera = remainingCameras[0];
        const nextCameraId = nextCamera._id || nextCamera.id;
        const nextCameraObj = nextCameraId
          ? scene.getObjectByName(nextCameraId)
          : null;
        if (nextCameraObj) {
          store.dispatch(selected_object(nextCameraObj));
          selectObjectInScene(nextCameraId);
          setSelectedObjectState(nextCameraObj);
        }
      } else {
        // No cameras left: clear selection
        dispatch(selected_object(null));
        setSelectedObjectState(null);
      }
    }

    setCameraToDelete(null);
    setShowCameraDeleteConfirmation(false);
  };

  const confirmParticleDeletion = () => {
    if (particleToDelete) {
      const obj = scene.getObjectByName(particleToDelete);
      if (obj) {
        cleanupParticleSystem(obj);

        if (obj.objType === 'terrain') {
          const rocksToRemove = scene.children.filter(
            (child) =>
              child.objType === 'rock' &&
              child.userData &&
              child.userData.parentTerrainId === particleToDelete
          );
          rocksToRemove.forEach((rock) => {
            scene.remove(rock);
            dispatch(removeIntersectedObject(rock));
            dispatch(removeSceneObject(rock));
            if (rock.userData && rock.userData.cleanup) {
              rock.userData.cleanup();
            }
          });

          if (obj.userData && obj.userData.cleanup) {
            obj.userData.cleanup();
          }
        }

        if (obj.parent) {
          obj.parent.remove(obj);
        }
      }

      dispatch(removeParticle(particleToDelete));
      transformControls.detach();
      dispatch(selected_object(null));
      setParticleToDelete(null);
    }
    setShowParticleDeleteConfirmation(false);
  };

  // Particle management functions
  const handleRemoveParticle = (id) => {
    setParticleToDelete(id);
    setShowParticleDeleteConfirmation(true);
  };

  const handleSelectParticle = (id) => {
    dispatch(setSelectedParticle(id));
  };

  // GLB mesh extraction
  const memoizedExtractMeshesFromGLB = useCallback(
    (glbAsset) => extractMeshesFromGLB(glbAsset, scene),
    [scene]
  );

  // Effects
  useEffect(() => {
    setChildren(scene?.children || []);
  }, [scene?.children]);

  useEffect(() => {
    setLastSaved(lastSavedSceneSnapshot);
  }, [lastSavedSceneSnapshot]);

  useEffect(() => {
    // During publishing, don't clear/update the GLB meshes structure to prevent UI jumping.
    // The scene is cleared and re-initialized during publish, so skipping this
    // prevents the asset list from flickering to an empty state.
    if (isPublishing) return;

    const newGLBMeshes = {};
    assets_data?.mesh?.forEach((meshAsset) => {
      const pathOrName =
        meshAsset.asset_file_path ||
        meshAsset.file_name ||
        meshAsset.url ||
        '';
      if (
        typeof pathOrName === 'string' &&
        pathOrName.toLowerCase().includes('.glb')
      ) {
        const meshes = memoizedExtractMeshesFromGLB(meshAsset);
        if (meshes.length > 0) {
          newGLBMeshes[meshAsset._id || meshAsset.name] = meshes;
        }
      }
    });

    setGLBMeshes(newGLBMeshes);
  }, [
    scene?.children,
    assets_data?.mesh,
    memoizedExtractMeshesFromGLB,
    scene,
    isPublishing,
  ]); // Add scene as dependency to trigger on hierarchy changes

  // Utility functions
  const toggleCollapsed = (itemId) => {
    setCollapsedItems((prevItems) => {
      const newItems = new Set(prevItems);
      if (newItems.has(itemId)) {
        newItems.delete(itemId);
      } else {
        newItems.add(itemId);
      }
      return newItems;
    });
  };

  const handleTogglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const getAssetId = (asset) => asset?._id || asset?.url || asset?.name;

  const getObjectByAsset = (asset) => {
    const id = getAssetId(asset);
    if (!id) return null;
    let obj = scene?.getObjectByName(id);
    if (!obj && asset?.asset_file_name) {
      obj = scene?.getObjectByName(asset.asset_file_name);
    }
    return obj;
  };

  const allAssets = useMemo(() => {
    const types = ['image', 'mesh', 'video', 'audio', 'text', 'hotspot', 'empty'];
    const list = [];
    const seenIds = new Set();

    const assetExistsInScene = (item) => {
      if (!scene || !item) return false;
      return collectAssetLookupIds(item).some((id) => !!scene.getObjectByName(id));
    };

    types.forEach((t) => {
      (assets_data?.[t] || []).forEach((item) => {
        const lookupIds = collectAssetLookupIds(item);
        if (!lookupIds.length) return;
        if (lookupIds.some((id) => seenIds.has(id))) return;
        if (!hasDisplayName(item) && !assetExistsInScene(item)) return;

        lookupIds.forEach((id) => seenIds.add(id));
        list.push({ item, type: t });
      });
    });

    // Add cameras with proper structure
    if (camera_properties) {
      camera_properties.forEach((camera) => {
        const stableId = getCameraStableId(camera);
        const label =
          camera.name || camera.file_name || camera.temp_file_name || 'Camera';
        list.push({
          item: {
            ...camera,
            _id: stableId,
            id: stableId,
            name: label,
            file_name: label,
          },
          type: 'camera',
        });
      });
    }

    // Add lights with proper structure
    if (light_data) {
      light_data.forEach((light) => {
        
        list.push({
          item: {
            ...light,
            _id: light.id || light._id,
            name: light.name || light.temp_file_name,
            file_name: light.name || light.temp_file_name,
          },
          type: 'light',
        });
      });
    }

    // Add particles with proper structure
    if (particles) {
      particles.forEach((particle) => {
        list.push({
          item: {
            ...particle,
            _id: particle.id,
            name: particle.name,
            file_name: particle.name,
          },
          type: 'particle',
        });
      });
    }

    return list;
  }, [assets_data, camera_properties, light_data, particles, scene?.children]); // Add camera_properties as dependency

  return {
    // State
    selectedPop,
    setselectedPop,
    collapsedItems,
    setCollapsedItems,
    isPopupOpen,
    setIsPopupOpen,
    projectConfirmation,
    setProjectConfirmation,
    children,
    setChildren,
    lastSaved,
    setLastSaved,
    isRenaming,
    setIsRenaming,
    newName,
    setNewName,
    submenuPosition,
    setSubmenuPosition,
    renamingItem,
    setRenamingItem,
    expandedGLBs,
    setExpandedGLBs,
    glbMeshes,
    setGLBMeshes,
    selectedGLBMeshPop,
    setSelectedGLBMeshPop,
    glbMeshSubmenuPosition,
    setGLBMeshSubmenuPosition,
    selectedLightPop,
    setSelectedLightPop,
    isRenamingLight,
    setIsRenamingLight,
    renamingLight,
    setRenamingLight,
    newLightName,
    setNewLightName,
    lightSubmenuPosition,
    setLightSubmenuPosition,
    showLightDeleteConfirmation,
    setShowLightDeleteConfirmation,
    lightToDelete,
    setLightToDelete,
    copiedItem,
    setCopiedItem,
    selectedCameraPop,
    setSelectedCameraPop,
    cameraSubmenuPosition,
    setCameraSubmenuPosition,
    showCameraDeleteConfirmation,
    setShowCameraDeleteConfirmation,
    cameraToDelete,
    setCameraToDelete,
    cameraRemoved,
    setCameraRemoved,
    draggingAssetId,
    setDraggingAssetId,
    hoverTargetAssetId,
    setHoverTargetAssetId,
    collapsedMap,
    setCollapsedMap,
    draggedFileName,
    setDraggedFileName,

    // Functions
    stopAllAnimations,
    startAllAnimations,
    handleRemoveLight,
    confirmLightDeletion,
    handleRemoveCamera,
    confirmCameraDeletion,
    handleRemoveParticle,
    handleSelectParticle,
    toggleCollapsed,
    handleTogglePopup,
    getAssetId,
    getObjectByAsset,
    allAssets,
    showParticleDeleteConfirmation,
    setShowParticleDeleteConfirmation,
    particleToDelete,
    setParticleToDelete,
    confirmParticleDeletion,

    // Redux state
    assets_data,
    select_object,
    light_data,
    camera_properties,
    particles,
    selectedLightId,
    dispatch,
  };
};
